import { useCallback, useId } from "react";
import type { Accept, FileRejection } from "react-dropzone";
import type { CaseDocument } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { NativeSelect, SaveError, cn, notifySaveFailed } from "@ncp/ui";

/** The formats an officer files a document in: PDF, Word (.docx), Excel (.xlsx), text, CSV and images – read page by page (substantive review 6.12). */
export const FILING_ACCEPT: Accept = {
  "application/pdf": [".pdf"], "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"], "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/plain": [".txt", ".md"], "text/csv": [".csv"], "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"],
};

const LEGACY_WORD = /\.(doc|dot|rtf)$/i;
const LEGACY_OFFICE = /\.(xls|ppt)$/i;

/**
 * Why a file was not taken, in the officer's language – never a file dropped and silently ignored: a legacy Word file with why it cannot be read
 * and what to save it as (there is no reader for the old binary format), an older Excel or PowerPoint file likewise, any other type with the
 * formats accepted.
 */
export function useFileRefusal() {
  const { t } = useTranslation();
  const explain = useCallback((name: string, code?: string) => {
    if (LEGACY_WORD.test(name)) return t("backoffice:filingUploads.legacyWord", { name });
    if (LEGACY_OFFICE.test(name)) return t("backoffice:filingUploads.legacyOffice", { name });
    if (code === "file-too-large") return t("backoffice:filingUploads.tooLarge", { name });
    return t("backoffice:filingUploads.refused", { name });
  }, [t]);
  const onRejected = useCallback((rejections: FileRejection[]) => {
    // Reported through the shared save helper: the file was not taken, with the reason as the cause's detail (S 2.8).
    for (const r of rejections) notifySaveFailed(new SaveError("refused", explain(r.file.name, r.errors[0]?.code)), t("backoffice:filingUploads.notUploaded"));
  }, [explain, t]);
  /** For a plain file input: true when the file may go, else the refusal is shown and false returned. */
  const check = useCallback((file: File) => {
    const lower = file.name.toLowerCase();
    if (Object.values(FILING_ACCEPT).some((exts) => (typeof exts === "string" ? [exts] : exts).some((ext: string) => lower.endsWith(ext)))) return true;
    notifySaveFailed(new SaveError("refused", explain(file.name)), t("backoffice:filingUploads.notUploaded"));
    return false;
  }, [explain, t]);
  return { explain, onRejected, check };
}

/**
 * "Translation of": files the document as the translation of one already on the file (substantive review 6.13). The two are compared once both
 * are read, the differences marked on the Documents tab; left on "Not a translation", the document stands on its own.
 */
export function TranslationOfSelect({ documents, value, onChange, disabled, className }: {
  documents: ReadonlyArray<Pick<CaseDocument, "id" | "fileName">>;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  if (documents.length === 0) return null;
  return (
    <div className={cn("space-y-1", className)}>
      <label htmlFor={id} className="block text-xs font-medium text-sand-700">{t("backoffice:filingUploads.translationOf")}</label>
      <NativeSelect id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="h-9 text-xs" title={t("backoffice:filingUploads.translationHint")}>
        <option value="">{t("backoffice:filingUploads.notATranslation")}</option>
        {documents.map((d) => <option key={d.id} value={d.id}>{d.fileName}</option>)}
      </NativeSelect>
    </div>
  );
}
